<script setup lang="ts">
import { computed } from 'vue';
import { useRecordPageContext } from '@/composables/recordPageContext';
import { Check, ChevronDown } from 'lucide-vue-next';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * The inspector tab switcher as a single pull-down (iOS-style): a text button
 * showing the active section's label with a chevron, opening a menu of the
 * sections (checkmark on the active one). Replaces a horizontal tab bar where a
 * compact, low-chrome switch is wanted; drives the same value the sibling
 * <TabsContent>s read, so panels still show/hide via the <Tabs> root.
 *
 * A small dot on the trigger flags a badge (e.g. pending commands) sitting on a
 * section that isn't the active one, so it's visible without opening the menu.
 */
interface InspectorTabItem {
    value: string;
    label: string;
    /** Optional count shown as a pill beside the label (and dot on the trigger when hidden). */
    badge?: number | null;
}

const props = defineProps<{
    modelValue: string;
    items: InspectorTabItem[];
}>();

const emit = defineEmits<{ (e: 'update:modelValue', value: string): void }>();

const activeItem = computed(
    () => props.items.find((item) => item.value === props.modelValue) ?? props.items[0],
);

const hasHiddenBadge = computed(() =>
    props.items.some((item) => item.value !== props.modelValue && (item.badge ?? 0) > 0),
);

/** Wherever RecordBody draws a record, the sections draw as real tabs (SAU-437). */
const recordPage = useRecordPageContext();

function select(value: string) {
    if (value !== props.modelValue) {
        emit('update:modelValue', value);
        recordPage?.onSectionChange(value);
    }
}
</script>

<template>
    <div
        v-if="recordPage"
        role="tablist"
        aria-label="Record sections"
        class="flex gap-6 border-b border-border text-sm"
        data-testid="record-tabs"
    >
        <button
            v-for="item in items"
            :key="item.value"
            type="button"
            role="tab"
            :aria-selected="item.value === modelValue"
            :data-testid="`record-tab-${item.value}`"
            class="-mb-px inline-flex h-10 items-center gap-1.5 rounded-t-sm border-b-2 border-transparent text-muted-foreground outline-none hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring aria-selected:border-accent-border aria-selected:text-foreground"
            @click="select(item.value)"
        >
            {{ item.label }}
            <span
                v-if="(item.badge ?? 0) > 0"
                class="rounded-full bg-accent px-1.5 text-[10px] font-semibold text-accent-foreground tabular-nums"
            >
                {{ item.badge }}
            </span>
        </button>
    </div>
    <DropdownMenu v-else>
        <DropdownMenuTrigger as-child>
            <button
                type="button"
                class="text-foreground hover:text-primary focus-visible:text-primary inline-flex items-center gap-1.5 text-base leading-tight font-semibold focus-visible:outline-none"
                data-testid="inspector-tabs-menu"
            >
                {{ activeItem?.label }}
                <span
                    v-if="(activeItem?.badge ?? 0) > 0"
                    class="bg-primary/15 text-primary rounded-full px-1.5 text-[10px] font-semibold tabular-nums"
                >
                    {{ activeItem?.badge }}
                </span>
                <ChevronDown class="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
                <span v-if="hasHiddenBadge" class="bg-primary size-1.5 rounded-full" aria-hidden="true" />
            </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" class="min-w-[11rem]">
            <DropdownMenuItem
                v-for="item in items"
                :key="item.value"
                :data-testid="`inspector-tab-option-${item.value}`"
                @click="select(item.value)"
            >
                {{ item.label }}
                <span
                    v-if="(item.badge ?? 0) > 0"
                    class="bg-primary/15 text-primary ml-2 rounded-full px-1.5 text-[10px] font-semibold tabular-nums"
                >
                    {{ item.badge }}
                </span>
                <Check v-if="item.value === modelValue" class="ml-auto size-4" aria-hidden="true" />
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
